<template>
  <transition name="fade">
    <div v-if="state.visible" class="daily-checkin-modal fixed inset-0 z-[1000] flex items-center justify-center px-4">
      <!-- backdrop -->
      <div class="absolute inset-0 bg-black/60 backdrop-blur-[2px]" @click="close"></div>

      <!-- dialog (漸層背景 + 柔光在 _daily_checkin_modal.scss 的 &__dialog；邊框/圓角與購買 modal 一致) -->
      <div class="daily-checkin-modal__dialog relative w-full max-w-[400px] rounded-8px border border-[#58475A]
                  px-6 pt-8 pb-6 flex flex-col gap-5">
        <button type="button"
                class="absolute top-3.5 right-4 text-white/55 hover:text-white text-22px leading-none transition-colors"
                aria-label="Close"
                @click="close">×</button>

        <!-- header -->
        <div class="flex flex-col items-center gap-3">
          <!-- 圓底/ring 用 Tailwind，accent 光暈在 scss 的 &__badge -->
          <div class="daily-checkin-modal__badge w-16 h-16 rounded-full bg-[#66323E] ring-1 ring-[#ff8971]/20
                      flex items-center justify-center">
            <img src="../../images/icons/checkin_calendar.svg" alt="" class="w-8 h-8" />
          </div>
          <h2 class="text-20px font-bold text-white">{{ t('title') }}</h2>
        </div>

        <!-- stats -->
        <div class="grid grid-cols-2">
          <div class="flex flex-col items-center gap-1 border-r border-white/10">
            <span class="flex items-center gap-2">
              <img src="../../images/icons/checkin_streak.svg" alt="" class="w-5 h-5" />
              <span class="text-[30px] font-bold text-white leading-none">{{ state.status.current_streak }}</span>
            </span>
            <span class="text-12px text-white/60">{{ t('streak_pill_label') }}</span>
          </div>
          <div class="flex flex-col items-center gap-1">
            <span class="flex items-center gap-2">
              <img src="../../images/icons/checkin_gift.svg" alt="" class="w-5 h-5" />
              <span class="text-[30px] font-bold text-[#f5b544] leading-none">{{ state.status.today_reward }}</span>
            </span>
            <span class="text-12px text-white/60">{{ t('reward_pill_label') }}</span>
          </div>
        </div>

        <div class="flex flex-col gap-2.5">
          <!-- weekly progress header -->
          <div class="flex items-center justify-between text-12px">
            <span class="text-white font-bold">{{ t('progress_title') }}</span>
            <span class="text-white/55">{{ claimedCount }}/{{ cycleLength }} {{ t('days_unit') }}</span>
          </div>

          <!-- 7-day calendar（今日格的發光在 scss 的 .daily-checkin-day--today） -->
          <div class="grid grid-cols-7 gap-1.5">
            <div
              v-for="box in state.status.calendar"
              :key="box.day"
              class="flex flex-col items-center justify-center gap-1 rounded-[10px] py-2 px-0.5 text-center"
              :class="boxClass(box)"
            >
              <span class="text-10px leading-none whitespace-nowrap" :class="dayClass(box)">
                {{ t('day_label', { day: box.day }) }}
              </span>
              <span class="text-10px font-bold leading-none whitespace-nowrap" :class="rewardClass(box)">
                +{{ box.reward }} {{ t('reward_unit') }}
              </span>
            </div>
          </div>
        </div>

        <!-- purchase cross-sell hint（漸層背景 + 邊框在 scss 的 &__hint） -->
        <div class="daily-checkin-modal__hint flex items-center gap-2 rounded-[10px] px-3 py-2.5">
          <img src="../../images/icons/checkin_bag.svg" alt="" class="w-4 h-4 shrink-0" />
          <p class="text-12px text-white/85 leading-5">
            <span class="font-bold text-orange-5">{{ t('hint_label') }}：</span>{{ t('purchase_hint') }}
          </p>
        </div>

        <div class="flex flex-col gap-3">
          <!-- primary CTA: complete check-in, then cross-sell to shop -->
          <button type="button"
                  :disabled="state.submitting"
                  class="btn btn-primary w-full text-14px font-bold flex items-center justify-center gap-2 py-2.5"
                  @click="checkInAndShop">
            <img src="../../images/icons/checkin_bag_white.svg" alt="" class="w-4 h-4" />
            {{ t('shop_cta') }}
          </button>

          <button type="button"
                  class="text-13px text-gray-150 hover:text-white text-center transition-colors"
                  @click="close">
            {{ t('later_label') }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { defineComponent, reactive, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue'

const I18N_PREFIX = 'private_profiles.my_point.daily_checkin_modal'

// 略過後在這段時間內不自動彈出（首頁自動彈出情境）。不做即時計時器：
// 略過時寫入 localStorage，5 分鐘內的自動彈出判斷會略過，過期後下次換頁/重整才再彈。
const SNOOZE_KEY = 'daily_checkin_snoozed_until'
const SNOOZE_MS = 5 * 60 * 1000 // 5 minutes

export default defineComponent({
  name: 'DailyCheckinModal',

  props: {
    initialStatus: { type: Object, default: null },
    autoOpen: { type: Boolean, default: false }
  },

  setup(props) {
    // 包一層，集中 i18n key 前綴，模板用 t('title') 即可
    const { proxy } = getCurrentInstance()
    const t = (key, opts) => proxy.$t(`${I18N_PREFIX}.${key}`, opts)

    const shopPath = '/posts'

    const state = reactive({
      visible: false,
      submitting: false,
      justClaimed: null,
      status: props.initialStatus || {
        available: false,
        logged_in: false,
        checked_in_today: false,
        show_popup: false,
        current_streak: 0,
        today_reward: 0,
        cycle_length: 7,
        calendar: []
      }
    })

    const cycleLength = computed(() => state.status.cycle_length || (state.status.calendar || []).length || 7)
    const claimedCount = computed(() => (state.status.calendar || []).filter((b) => b.state === 'claimed').length)

    const csrfToken = () => {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : ''
    }

    const fetchStatus = async () => {
      try {
        const res = await fetch('/api/v1/daily_checkins/status', {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'same-origin'
        })
        if (res.ok) {
          const body = await res.json()
          if (body.data) state.status = body.data
        }
      } catch (err) {
        console.error('Failed to load check-in status:', err)
      }
    }

    // 打卡：使用者按下主要 CTA 才完成今日簽到並發點（popup 出現時僅顯示「點下去會拿到」
    // 的預期天數/獎勵）。冪等由後端保證；已簽或送出中則直接略過。
    // 回傳後端給的導購路徑（最近上架的隨機 post），沒有則回 null 由呼叫端 fallback。
    const checkIn = async () => {
      if (state.status.checked_in_today || state.submitting) return null
      state.submitting = true
      try {
        const res = await fetch('/api/v1/daily_checkins', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken()
          },
          credentials: 'same-origin'
        })
        const body = await res.json().catch(() => ({}))
        if (res.ok) {
          if (body.data) state.status = body.data
          if (body.claimed) state.justClaimed = body.claimed.amount
          return body.redirect_path || null
        } else if (res.status === 409 && body.data) {
          // 已簽到（例如其他分頁先簽）：同步成最新狀態即可
          state.status = body.data
        }
      } catch (err) {
        console.error('Check-in failed:', err)
      } finally {
        state.submitting = false
      }
      return null
    }

    // 主要 CTA：未登入先導去登入（登入後回首頁，popup 會再出現由使用者按鈕簽到）；
    // 已登入則完成今日簽到（發點，冪等）後，隨機導去一篇最近上架的 post 讓他瀏覽
    // （後端挑選；沒有候選時 fallback 到精選列表）。
    const checkInAndShop = async () => {
      if (!state.status.logged_in) {
        window.location.href = '/users/sign_in'
        return
      }
      const redirectPath = await checkIn()
      // 打卡成功：把已翻譯好的成功訊息寫入 sessionStorage，導購跳轉後由目標頁的
      // initDailyCheckinToast（application.js）於右下角顯示 toast，取代在 modal 內跳文案。
      if (state.justClaimed) {
        try {
          window.sessionStorage.setItem('daily_checkin_toast', t('success_toast', { amount: state.justClaimed }))
        } catch (err) {
          // sessionStorage 不可用（如無痕模式）時略過，不影響跳轉
        }
      }
      window.location.assign(redirectPath || shopPath)
    }

    const open = async () => {
      await fetchStatus()
      state.visible = true
    }

    const isSnoozed = () => {
      try {
        return Date.now() < parseInt(window.localStorage.getItem(SNOOZE_KEY) || '0', 10)
      } catch (err) {
        return false
      }
    }

    const snooze = () => {
      try {
        window.localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS))
      } catch (err) {
        // localStorage 不可用（如無痕模式）時略過，不影響關閉行為
      }
    }

    // ×、背景、稍後再說 三種關閉都算「略過」→ 寫入 5 分鐘略過。
    const close = () => {
      snooze()
      state.visible = false
    }

    // 日曆格子配色用 Tailwind 單色即可；今日格另掛 daily-checkin-day--today，
    // 由 _daily_checkin_modal.scss 補上多層發光陰影。
    const boxClass = (box) => {
      switch (box.state) {
        case 'claimed': return 'bg-[#FF8971]/30'
        case 'today': return 'bg-primary ring-1 ring-[#ff8971]/60 daily-checkin-day--today'
        default: return 'bg-gray-500'
      }
    }

    const dayClass = (box) => {
      switch (box.state) {
        case 'claimed': return 'text-orange-5'
        case 'today': return 'text-white'
        default: return 'text-gray-150'
      }
    }

    const rewardClass = (box) => {
      switch (box.state) {
        case 'today': return 'text-white'
        case 'claimed': return 'text-orange-5'
        default: return 'text-gray-150'
      }
    }

    const onExternalOpen = () => open()

    onMounted(async () => {
      window.addEventListener('daily-checkin:open', onExternalOpen)
      document.querySelectorAll('[data-daily-checkin-open]').forEach((el) => {
        el.addEventListener('click', onExternalOpen)
      })
      // 自動彈出僅在「任務上線且該彈」且「不在 5 分鐘略過內」時觸發；
      // 由 [data-daily-checkin-open] / daily-checkin:open 的手動開啟不受略過影響。
      if (props.autoOpen && state.status.show_popup && !isSnoozed()) {
        state.visible = true
      }
    })

    onUnmounted(() => {
      window.removeEventListener('daily-checkin:open', onExternalOpen)
    })

    return { state, t, shopPath, cycleLength, claimedCount, close, checkInAndShop, boxClass, dayClass, rewardClass }
  }
})
</script>
